import { t } from "@travily/i18n";
import { showToast } from "@ui/components/Toast";
import type { Bbox } from "./where-bounding-boxes";

const NEARBY_RADIUS_KM = 100;
const KM_PER_DEG_LAT = 111;
const TIMEOUT_MS = 10_000;

function notifyUnavailable(): null {
  showToast.warning(
    t("web.programs.index.nearby_unavailable_title"),
    t("web.programs.index.nearby_unavailable_description"),
  );
  return null;
}

function bboxAround(latitude: number, longitude: number): Bbox {
  const dLat = NEARBY_RADIUS_KM / KM_PER_DEG_LAT;
  // Longitude degrees shrink with latitude; clamp the cosine to keep the box
  // sane near the poles.
  const dLon = NEARBY_RADIUS_KM / (KM_PER_DEG_LAT * Math.max(Math.cos((latitude * Math.PI) / 180), 0.01));
  const west = Math.max(-180, longitude - dLon);
  const east = Math.min(180, longitude + dLon);
  const south = Math.max(-90, latitude - dLat);
  const north = Math.min(90, latitude + dLat);
  return [west, south, east, north];
}

export async function requestNearbyBoundingBox(): Promise<Bbox | null> {
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    return notifyUnavailable();
  }

  return new Promise<Bbox | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(bboxAround(position.coords.latitude, position.coords.longitude)),
      () => resolve(notifyUnavailable()),
      { enableHighAccuracy: false, timeout: TIMEOUT_MS, maximumAge: 5 * 60_000 },
    );
  });
}
